import { useState, useCallback, useEffect } from 'react';
import { api } from '@expat-ui/shared';
import { getFreshAccessToken } from '../../utils/authUtils';
import { getExchangeRates } from '../../utils/exchangeRates';
import { useCashflows, useProfile, useResidencyCountries } from '../../hooks/useApi';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import {
    ProjectionResult,
    Country,
    Currency,
    GoalFundingSummary,
    GOAL_CATEGORY_LABELS,
    CURRENCY_SYMBOLS,
    COUNTRY_CURRENCIES,
    CASHFLOW_CATEGORY_LABELS,
} from '../../types/api.types';
import { formatAmount } from '../../utils/numberFormat';
import styles from './SimulatorPage.module.scss';

const ALL_RETIREMENT_LOCATIONS: { value: Country; label: string }[] = [
    { value: 'INDIA', label: 'India (INR)' },
    { value: 'US', label: 'United States (USD)' },
    { value: 'CAN', label: 'Canada (CAD)' },
];

const DEFAULT_INFLATION: Record<Country, number> = { INDIA: 6, US: 3, CAN: 2.5 };
const DEFAULT_LIFE_EXPECTANCY: Record<Country, number> = { INDIA: 80, US: 83, CAN: 84 };

const SWR_OPTIONS = [3, 3.5, 4, 4.5, 5];

export function SimulatorPage() {
    const [retirementAge, setRetirementAge] = useState(55);
    const [retirementCountry, setRetirementCountry] = useState<Country>('INDIA');
    const [inflationRate, setInflationRate] = useState(DEFAULT_INFLATION['INDIA']);
    const [lifeExpectancy, setLifeExpectancy] = useState(DEFAULT_LIFE_EXPECTANCY['INDIA']);
    const [extraSpending, setExtraSpending] = useState<number>(0);
    const [selectedSWR, setSelectedSWR] = useState(4);
    const [result, setResult] = useState<ProjectionResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { cashflows } = useCashflows();
    const { profile } = useProfile();
    const { countries: residencyCountries, retirementCountry: profileRetirementCountry } = useResidencyCountries();
    const retirementLocations =
        residencyCountries.length > 0
            ? ALL_RETIREMENT_LOCATIONS.filter((l) => residencyCountries.includes(l.value))
            : ALL_RETIREMENT_LOCATIONS;

    useEffect(() => {
        if (profileRetirementCountry) {
            setRetirementCountry(profileRetirementCountry);
            setInflationRate(DEFAULT_INFLATION[profileRetirementCountry]);
            setLifeExpectancy(DEFAULT_LIFE_EXPECTANCY[profileRetirementCountry]);
        }
    }, [profileRetirementCountry]);

    const { convertCurrency } = useExchangeRates();
    const baseCurrency: Currency = profile?.baseCurrency ?? 'INR';

    const retirementBudgetItems = cashflows.filter(
        (cf) => cf.type === 'EXPENSE' && !cf.isOneTime && cf.isRetirementExpense,
    );
    const ongoingObligations = cashflows.filter(
        (cf) =>
            cf.type === 'EXPENSE' &&
            !cf.isOneTime &&
            !cf.isRetirementExpense &&
            !cf.endsAtRetirement &&
            cf.endAge > retirementAge,
    );
    const retirementCurrency = COUNTRY_CURRENCIES[retirementCountry];
    const retirementBudgetTotal = retirementBudgetItems.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency),
        0,
    );
    const ongoingObligationsTotal = ongoingObligations.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency),
        0,
    );
    const retirementOneTimeExpenses = cashflows.filter(
        (cf) => cf.type === 'EXPENSE' && cf.isRetirementExpense && cf.isOneTime,
    );
    const retirementOneTimeTitles = retirementOneTimeExpenses
        .map((cf) => cf.label ?? CASHFLOW_CATEGORY_LABELS[cf.category])
        .join(', ');
    const oneTimeEvents = cashflows.filter((cf) => cf.isOneTime);

    const extraSpendingInBase =
        extraSpending > 0 ? convertCurrency(extraSpending, retirementCurrency, baseCurrency) : 0;

    const runSimulation = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const fxRates = await getExchangeRates();
            const data = await api.post<ProjectionResult>(
                '/projection',
                {
                    retirementAge,
                    retirementCountry,
                    inflationRate: inflationRate / 100,
                    lifeExpectancy,
                    fxRates,
                    ...(extraSpending > 0 ? { withdrawalAmount: extraSpending } : {}),
                },
                token,
            );
            setResult(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Simulation failed');
        } finally {
            setLoading(false);
        }
    }, [retirementAge, retirementCountry, inflationRate, lifeExpectancy, extraSpending]);

    const formatCurrency = (amount: number, currency: string) => {
        return formatAmount(Math.round(amount), currency as Currency);
    };

    const swrPortfolio = result ? result.wealthAtRetirement : 0;
    const swrAnnualWithdrawal = swrPortfolio * (selectedSWR / 100);

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Retirement Simulator</h1>
                <p className={styles.subtitle}>
                    Adjust levers below and run a Monte Carlo simulation to project your retirement readiness.
                </p>
            </div>

            <div className={styles.controlPanel}>
                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>
                        Retirement Age
                        <span className={styles.sliderValue}>{retirementAge}</span>
                    </label>
                    <input
                        type="range"
                        className={styles.slider}
                        min={40}
                        max={70}
                        value={retirementAge}
                        onChange={(e) => setRetirementAge(Number(e.target.value))}
                    />
                    <div className={styles.sliderRange}>
                        <span>40</span>
                        <span>70</span>
                    </div>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>Primary Retirement Location</label>
                    <select
                        className={styles.select}
                        value={retirementCountry}
                        onChange={(e) => {
                            const country = e.target.value as Country;
                            setRetirementCountry(country);
                            setInflationRate(DEFAULT_INFLATION[country]);
                            setLifeExpectancy(DEFAULT_LIFE_EXPECTANCY[country]);
                        }}
                    >
                        {retirementLocations.map((loc) => (
                            <option key={loc.value} value={loc.value}>
                                {loc.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>
                        Inflation Rate
                        <span className={styles.sliderValue}>{inflationRate}%</span>
                    </label>
                    <input
                        type="range"
                        className={styles.slider}
                        min={1}
                        max={10}
                        step={0.5}
                        value={inflationRate}
                        onChange={(e) => setInflationRate(Number(e.target.value))}
                    />
                    <div className={styles.sliderRange}>
                        <span>1%</span>
                        <span>10%</span>
                    </div>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>
                        Life Expectancy
                        <span className={styles.sliderValue}>{lifeExpectancy} yrs</span>
                    </label>
                    <input
                        type="range"
                        className={styles.slider}
                        min={75}
                        max={100}
                        value={lifeExpectancy}
                        onChange={(e) => setLifeExpectancy(Number(e.target.value))}
                    />
                    <div className={styles.sliderRange}>
                        <span>75</span>
                        <span>100</span>
                    </div>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>Retirement Expenses (from Cashflows)</label>
                    <div className={styles.expenseSummary}>
                        {retirementBudgetItems.length === 0 && ongoingObligations.length === 0 ? (
                            <div className={styles.expenseEmpty}>
                                No retirement expenses set up. Add them in Cashflows.
                            </div>
                        ) : (
                            <>
                                {retirementBudgetItems.map((cf, i) => (
                                    <div key={`ret-${i}`} className={styles.expenseItem}>
                                        <span className={styles.expenseItemName}>
                                            <span className={styles.expenseItemText}>
                                                {cf.label || CASHFLOW_CATEGORY_LABELS[cf.category] || cf.category}
                                                {cf.label && (
                                                    <span className={styles.expenseItemCategory}>
                                                        {CASHFLOW_CATEGORY_LABELS[cf.category] || cf.category}
                                                    </span>
                                                )}
                                            </span>
                                            <span className={`${styles.expenseBadge} ${styles.retirementBadge}`}>
                                                Retirement
                                            </span>
                                        </span>
                                        <span>
                                            {formatAmount(
                                                Math.round(convertCurrency(cf.amount, cf.currency, baseCurrency)),
                                                baseCurrency,
                                            )}
                                            /mo
                                        </span>
                                    </div>
                                ))}
                                {retirementBudgetItems.length > 0 && (
                                    <div className={styles.expenseSubtotal}>
                                        <span>Retirement Budget</span>
                                        <span>
                                            {CURRENCY_SYMBOLS[baseCurrency]}
                                            {(
                                                (retirementBudgetTotal * 12) /
                                                (baseCurrency === 'INR' ? 100000 : 1000)
                                            ).toFixed(1)}
                                            {baseCurrency === 'INR' ? 'L' : 'K'}/yr
                                        </span>
                                    </div>
                                )}
                                {ongoingObligations.length > 0 && (
                                    <>
                                        {retirementBudgetItems.length > 0 && <div className={styles.expenseDivider} />}
                                        {ongoingObligations.map((cf, i) => (
                                            <div key={`obl-${i}`} className={styles.expenseItem}>
                                                <span className={styles.expenseItemName}>
                                                    <span className={styles.expenseItemText}>
                                                        {cf.label ||
                                                            CASHFLOW_CATEGORY_LABELS[cf.category] ||
                                                            cf.category}
                                                        {cf.label && (
                                                            <span className={styles.expenseItemCategory}>
                                                                {CASHFLOW_CATEGORY_LABELS[cf.category] || cf.category}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span
                                                        className={`${styles.expenseBadge} ${styles.obligationBadge}`}
                                                    >
                                                        Until {cf.endAge}
                                                    </span>
                                                </span>
                                                <span>
                                                    {formatAmount(
                                                        Math.round(
                                                            convertCurrency(cf.amount, cf.currency, baseCurrency),
                                                        ),
                                                        baseCurrency,
                                                    )}
                                                    /mo
                                                </span>
                                            </div>
                                        ))}
                                        <div className={styles.expenseSubtotal}>
                                            <span>Ongoing Obligations</span>
                                            <span>
                                                {CURRENCY_SYMBOLS[baseCurrency]}
                                                {(
                                                    (ongoingObligationsTotal * 12) /
                                                    (baseCurrency === 'INR' ? 100000 : 1000)
                                                ).toFixed(1)}
                                                {baseCurrency === 'INR' ? 'L' : 'K'}/yr
                                            </span>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>
                        Extra Annual Spending
                        <span className={styles.sliderValue}>
                            {extraSpending > 0 ? formatCurrency(extraSpending, retirementCurrency) : 'None'}
                        </span>
                    </label>
                    <input
                        type="range"
                        className={styles.slider}
                        min={0}
                        max={20000000}
                        step={100000}
                        value={extraSpending}
                        onChange={(e) => setExtraSpending(Number(e.target.value))}
                    />
                    <div className={styles.sliderRange}>
                        <span>None</span>
                        <span>2 Cr</span>
                    </div>
                    {extraSpending > 0 && retirementCurrency !== baseCurrency && (
                        <div className={styles.sliderCurrencyNote}>
                            ≈ {formatCurrency(extraSpendingInBase, baseCurrency)} in {baseCurrency}
                        </div>
                    )}
                </div>

                <div className={styles.controlActions}>
                    <button className={styles.runButton} onClick={runSimulation} disabled={loading}>
                        {loading ? 'Running Simulation...' : 'Run Simulation'}
                    </button>
                </div>
            </div>

            {error && <p className={styles.error}>{error}</p>}

            {result && (
                <div className={styles.results}>
                    <div className={styles.verifiedBadge}>
                        <span className={styles.verifiedIcon}>✓</span>
                        Monte Carlo Verified — 1,000 simulations
                    </div>

                    <div className={styles.metricsRow}>
                        <div
                            className={styles.metricCard}
                            title={`Out of 1,000 simulated market scenarios, ${result.successRate}% sustained your withdrawals through age ${lifeExpectancy} without running out of money. The remaining ${100 - result.successRate}% failed due to poor sequence-of-returns (e.g., a market crash early in retirement).`}
                        >
                            <span className={styles.metricLabel}>SUCCESS PROBABILITY</span>
                            <span
                                className={`${styles.metricValue} ${result.successRate >= 80 ? styles.good : result.successRate >= 60 ? styles.moderate : styles.poor}`}
                            >
                                {result.successRate}%
                            </span>
                            <span className={styles.metricStatus}>
                                {result.successRate >= 80
                                    ? 'Stable'
                                    : result.successRate >= 60
                                      ? 'Moderate Risk'
                                      : 'High Risk'}
                            </span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>SAFE ANNUAL SPEND</span>
                            <span className={styles.metricValue}>
                                {formatCurrency(result.safeAnnualSpend, result.baseCurrency)}
                            </span>
                            {retirementCurrency !== result.baseCurrency && (
                                <span className={styles.metricAlt}>
                                    ≈{' '}
                                    {formatCurrency(
                                        convertCurrency(
                                            result.safeAnnualSpend,
                                            result.baseCurrency,
                                            retirementCurrency,
                                        ),
                                        retirementCurrency,
                                    )}{' '}
                                    in {retirementCurrency}
                                </span>
                            )}
                            <span className={styles.metricStatus}>
                                Extra you can spend yearly beyond planned expenses (90% confidence)
                            </span>
                            {extraSpendingInBase > 0 &&
                                result.safeAnnualSpend > 0 &&
                                (() => {
                                    const pct = Math.round((extraSpendingInBase / result.safeAnnualSpend) * 100);
                                    const cls =
                                        pct < 75
                                            ? styles.comparisonGood
                                            : pct <= 100
                                              ? styles.comparisonCaution
                                              : styles.comparisonOver;
                                    const text =
                                        pct <= 100
                                            ? `Your current plan uses ${pct}% of your safe limit`
                                            : `Your current plan exceeds safe limit by ${pct - 100}%`;
                                    return <span className={`${styles.metricComparison} ${cls}`}>{text}</span>;
                                })()}
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>CURRENT NET WORTH</span>
                            <span className={styles.metricValue}>
                                {formatCurrency(result.startingWealth, result.baseCurrency)}
                            </span>
                            <span className={styles.metricStatus}>Today, across all accounts</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>WEALTH AT RETIREMENT</span>
                            <span className={styles.metricValue}>
                                {formatCurrency(result.wealthAtRetirement, result.baseCurrency)}
                            </span>
                            <span className={styles.metricStatus}>Projected at age {result.retirementAge}</span>
                        </div>
                    </div>

                    {result.insights && result.insights.length > 0 && (
                        <div className={styles.insightsSection}>
                            {result.insights.map((insight, idx) => (
                                <div key={idx} className={styles.insightItem}>
                                    <span className={styles.insightIcon}>💡</span>
                                    <span>{insight}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className={styles.swrSection}>
                        <div className={styles.swrHeader}>
                            <span className={styles.swrLabel}>Safe Withdrawal Rate Playground</span>
                        </div>
                        <div className={styles.swrButtons}>
                            {SWR_OPTIONS.map((swr) => (
                                <button
                                    key={swr}
                                    className={`${styles.swrButton} ${selectedSWR === swr ? styles.active : ''}`}
                                    onClick={() => setSelectedSWR(swr)}
                                >
                                    {swr}%
                                </button>
                            ))}
                        </div>
                        {swrPortfolio > 0 && (
                            <div className={styles.swrInfo}>
                                At {selectedSWR}% SWR, you can withdraw{' '}
                                {formatCurrency(Math.round(swrAnnualWithdrawal), result!.baseCurrency)}
                                /yr from your projected retirement portfolio of{' '}
                                {formatCurrency(Math.round(swrPortfolio), result!.baseCurrency)}.
                            </div>
                        )}
                    </div>

                    <div className={styles.projectionChart}>
                        <h3 className={styles.chartTitle}>Projected Net Worth Timeline</h3>
                        <div className={styles.chartContainer}>
                            {result.yearlyDataPoints.map((dp) => {
                                const totalNW =
                                    dp.netWorthByCountry.india + dp.netWorthByCountry.us + dp.netWorthByCountry.can;
                                const maxNW = Math.max(
                                    ...result.yearlyDataPoints.map(
                                        (p) =>
                                            p.netWorthByCountry.india +
                                            p.netWorthByCountry.us +
                                            p.netWorthByCountry.can,
                                    ),
                                );
                                const height = maxNW > 0 ? (totalNW / maxNW) * 100 : 0;
                                const isRetirement = dp.age === result.retirementAge;
                                const hasGoal = (dp.goalEvents?.length ?? 0) > 0;
                                const goalTitles = dp.goalEvents?.map((g) => g.label).join(', ');
                                const hasRetirementOneTime = retirementOneTimeExpenses.length > 0 && isRetirement;
                                const tooltipParts = [`Age ${dp.age}: ${formatCurrency(totalNW, result.baseCurrency)}`];
                                if (hasGoal) tooltipParts.push(`Goal: ${goalTitles}`);
                                if (hasRetirementOneTime)
                                    tooltipParts.push(`One-time expense: ${retirementOneTimeTitles}`);
                                return (
                                    <div
                                        key={dp.year}
                                        className={[
                                            styles.bar,
                                            dp.isShortfallDetected ? styles.shortfall : '',
                                            isRetirement ? styles.retirement : '',
                                            hasGoal ? styles.goalMarker : '',
                                            hasRetirementOneTime ? styles.retirementExpenseMarker : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        style={{ height: `${Math.max(2, height)}%` }}
                                        title={tooltipParts.join(' — ')}
                                    />
                                );
                            })}
                        </div>
                        <div className={styles.chartAxis}>
                            <span>Now</span>
                            <span>Retirement ({result.retirementAge})</span>
                            <span>Age {lifeExpectancy}</span>
                        </div>
                        <div className={styles.chartLegend}>
                            <div className={styles.legendItem}>
                                <div className={`${styles.legendColor} ${styles.normal}`} />
                                <span>Normal</span>
                            </div>
                            <div className={styles.legendItem}>
                                <div className={`${styles.legendColor} ${styles.retirement}`} />
                                <span>Retirement</span>
                            </div>
                            <div className={styles.legendItem}>
                                <div className={`${styles.legendColor} ${styles.shortfall}`} />
                                <span>Shortfall</span>
                            </div>
                            <div className={styles.legendItem}>
                                <span className={styles.legendIcon}>★</span>
                                <span>Goal Event</span>
                            </div>
                            <div className={styles.legendItem}>
                                <span className={`${styles.legendIcon} ${styles.legendIconInfo}`}>◆</span>
                                <span>One-time Retirement Expense</span>
                            </div>
                        </div>
                    </div>

                    {result.yearlyDataPoints.some(
                        (dp) =>
                            dp.projectedTaxByCountry.india +
                                dp.projectedTaxByCountry.us +
                                dp.projectedTaxByCountry.can >
                            0,
                    ) && (() => {
                        const pts = result.yearlyDataPoints;
                        const maxTax = Math.max(
                            ...pts.map(
                                (dp) =>
                                    dp.projectedTaxByCountry.india +
                                    dp.projectedTaxByCountry.us +
                                    dp.projectedTaxByCountry.can,
                            ),
                            1,
                        );
                        const W = 600, H = 160, PAD = 8;
                        const n = pts.length;
                        const xs = (i: number) => PAD + (i / Math.max(n - 1, 1)) * (W - 2 * PAD);
                        const ys = (v: number) => H - PAD - (v / maxTax) * (H - 2 * PAD);
                        const line = (key: 'india' | 'us' | 'can') =>
                            pts
                                .map((dp, i) => `${i === 0 ? 'M' : 'L'}${xs(i).toFixed(1)},${ys(dp.projectedTaxByCountry[key]).toFixed(1)}`)
                                .join(' ');
                        const totalByCountry = pts.reduce(
                            (acc, dp) => ({
                                india: acc.india + dp.projectedTaxByCountry.india,
                                us: acc.us + dp.projectedTaxByCountry.us,
                                can: acc.can + dp.projectedTaxByCountry.can,
                            }),
                            { india: 0, us: 0, can: 0 },
                        );
                        return (
                            <div className={styles.taxChart}>
                                <h3 className={styles.chartTitle}>Annual Tax Paid by Country</h3>
                                <svg viewBox={`0 0 ${W} ${H}`} className={styles.taxSvg}>
                                    {[0.25, 0.5, 0.75, 1].map((f) => (
                                        <line
                                            key={f}
                                            x1={PAD} x2={W - PAD}
                                            y1={ys(maxTax * f).toFixed(1)} y2={ys(maxTax * f).toFixed(1)}
                                            stroke="currentColor" strokeOpacity="0.1" strokeWidth="1"
                                        />
                                    ))}
                                    <path d={line('india')} fill="none" stroke="#FF9933" strokeWidth="2" strokeLinejoin="round" />
                                    <path d={line('us')} fill="none" stroke="#002366" strokeWidth="2" strokeLinejoin="round" />
                                    <path d={line('can')} fill="none" stroke="#D52B1E" strokeWidth="2" strokeLinejoin="round" />
                                </svg>
                                <div className={styles.chartAxis}>
                                    <span>Now</span>
                                    <span>Retirement ({result.retirementAge})</span>
                                    <span>Age {lifeExpectancy}</span>
                                </div>
                                <div className={styles.taxLegend}>
                                    {totalByCountry.india > 0 && (
                                        <div className={styles.taxLegendItem}>
                                            <div className={styles.taxDotIndia} />
                                            <span>India</span>
                                            <span className={styles.taxLegendAmt}>
                                                {formatCurrency(Math.round(totalByCountry.india), result.baseCurrency)} lifetime
                                            </span>
                                        </div>
                                    )}
                                    {totalByCountry.us > 0 && (
                                        <div className={styles.taxLegendItem}>
                                            <div className={styles.taxDotUS} />
                                            <span>US</span>
                                            <span className={styles.taxLegendAmt}>
                                                {formatCurrency(Math.round(totalByCountry.us), result.baseCurrency)} lifetime
                                            </span>
                                        </div>
                                    )}
                                    {totalByCountry.can > 0 && (
                                        <div className={styles.taxLegendItem}>
                                            <div className={styles.taxDotCAN} />
                                            <span>Canada</span>
                                            <span className={styles.taxLegendAmt}>
                                                {formatCurrency(Math.round(totalByCountry.can), result.baseCurrency)} lifetime
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {result.goalFundingSummary && result.goalFundingSummary.length > 0 && (
                        <div className={styles.goalsSection}>
                            <h3 className={styles.goalsSectionTitle}>Goal Outcomes</h3>
                            <div className={styles.goalsGrid}>
                                {result.goalFundingSummary.map((g: GoalFundingSummary) => {
                                    const fillClass =
                                        g.fundingRate >= 80 ? '' : g.fundingRate >= 50 ? styles.medium : styles.low;
                                    const isFunded = g.fundingRate >= 80;
                                    return (
                                        <div key={g.goalId} className={styles.goalCard}>
                                            <div className={styles.goalCardHeader}>
                                                <span className={styles.goalLabel}>{g.label}</span>
                                                <span
                                                    className={`${styles.goalStatusBadge} ${isFunded ? styles.funded : styles.atRisk}`}
                                                >
                                                    {isFunded ? 'On Track' : 'At Risk'}
                                                </span>
                                            </div>
                                            <p className={styles.goalMeta}>
                                                {GOAL_CATEGORY_LABELS[g.category]} &bull; Age {g.targetAge} &bull;{' '}
                                                {formatCurrency(g.targetAmount, result.baseCurrency)}
                                            </p>
                                            <div className={styles.goalFundingBar}>
                                                <div
                                                    className={`${styles.goalFundingFill} ${fillClass}`}
                                                    style={{ width: `${g.fundingRate}%` }}
                                                />
                                            </div>
                                            <p className={styles.goalFundingLabel}>
                                                Funded in {g.fundingRate}% of simulations
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {oneTimeEvents.length > 0 && (
                        <div className={styles.oneTimeSection}>
                            <h3 className={styles.oneTimeSectionTitle}>One-time Events</h3>
                            <div className={styles.oneTimeList}>
                                {oneTimeEvents.map((cf) => {
                                    const isIncome = cf.type === 'INCOME';
                                    const firesAtRetirement = cf.isRetirementExpense;
                                    const ageLabel = firesAtRetirement
                                        ? `at retirement (age ${retirementAge})`
                                        : `at age ${cf.startAge}`;
                                    return (
                                        <div key={cf.cashFlowId} className={styles.oneTimeItem}>
                                            <span
                                                className={`${styles.oneTimeSign} ${isIncome ? styles.income : styles.expense}`}
                                            >
                                                {isIncome ? '+' : '−'}
                                            </span>
                                            <span className={styles.oneTimeLabel}>
                                                {cf.label || CASHFLOW_CATEGORY_LABELS[cf.category]}
                                                <span className={styles.oneTimeCategory}>
                                                    {CASHFLOW_CATEGORY_LABELS[cf.category]}
                                                </span>
                                            </span>
                                            <span className={styles.oneTimeAmount}>
                                                {formatCurrency(cf.amount, cf.currency)}
                                            </span>
                                            <span className={styles.oneTimeAge}>{ageLabel}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {result.withdrawalSummary && result.withdrawalSummary.drawdownSequence.length > 0 && (
                        <div className={styles.drawdownSection}>
                            <h3 className={styles.drawdownTitle}>Recommended Drawdown Strategy</h3>
                            <p className={styles.drawdownSubtitle}>
                                Tax-optimized withdrawal sequence for your retirement residency
                            </p>

                            <div className={styles.sequenceList}>
                                {result.withdrawalSummary.drawdownSequence.map((entry) => (
                                    <div key={entry.accountType} className={styles.sequenceRow}>
                                        <span className={styles.sequenceRank}>{entry.priority}</span>
                                        <span className={styles.sequenceLabel}>{entry.label}</span>
                                        <span
                                            className={`${styles.taxBadge} ${
                                                entry.taxCategory === 'TAX_FREE'
                                                    ? styles.taxFree
                                                    : entry.taxCategory === 'CAPITAL_GAINS'
                                                      ? styles.capitalGains
                                                      : entry.taxCategory === 'TAX_DEFERRED'
                                                        ? styles.taxDeferred
                                                        : styles.highTax
                                            }`}
                                        >
                                            {entry.taxCategory.replace(/_/g, ' ')}
                                        </span>
                                        <span className={styles.sequenceRate}>
                                            {(entry.effectiveTaxRate * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.savingsRow}>
                                <div className={styles.savingsMetric}>
                                    <span className={styles.savingsLabel}>Effective Withdrawal Tax</span>
                                    <span className={styles.savingsValue}>
                                        {(result.withdrawalSummary.effectiveTaxRate * 100).toFixed(1)}%
                                    </span>
                                </div>
                                {result.withdrawalSummary.taxSavedVsNaive > 0 && (
                                    <div className={styles.savingsMetric}>
                                        <span className={styles.savingsLabel}>Est. Tax Savings</span>
                                        <span className={`${styles.savingsValue} ${styles.savings}`}>
                                            {formatCurrency(
                                                result.withdrawalSummary.taxSavedVsNaive,
                                                result.baseCurrency,
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
